import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import InviteListener from './components/InviteListener';
import UpdatePrompt from './components/UpdatePrompt';
import ConsentBanner from './components/ConsentBanner';
import AgeGate from './components/AgeGate';
import AppBackdrop from './components/AppBackdrop';
import './styles/theme.css';
import './styles/brandbook.css';
import './App.css';

// === ЧТО ГРУЗИТСЯ СРАЗУ, А ЧТО ПО ТРЕБОВАНИЮ (12.09.2026) ===
//
// Было: весь сайт одним куском `main.js` — 2,86 МБ (783 КБ в сжатом виде). Человек
// открывал ссылку «скачать приложение» и тянул заодно весь игровой код, Agora и админку:
// p95 страницы /get был 2,8 с (нашли по недельному отчёту Sentry).
//
// СРАЗУ (в главном куске) — только двери, куда приходят люди по ссылкам: лендинг,
// страница скачивания, правовые документы и удаление аккаунта. Их читают чаще всего,
// и именно они должны открываться мгновенно.
//
// ПО ТРЕБОВАНИЮ (своим куском) — всё, что за входом: игровой стол (он тянет socket.io
// и Agora), лобби, комната, меню, профиль, друзья, вход-регистрация и вся админка.
// Кусок подгружается при первом переходе на такую страницу и дальше лежит в кэше.
import LandingPage from './pages/LandingPage';
import GetPage from './pages/GetPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

// Правовые документы — свой кусок. Они публичные и обязательны (ссылки из сторов и из
// подвала), но открывают их редко, а тянут они за собой разборщик Markdown — это 300 КБ
// в главном куске за страницу, которую читают раз в жизни. Лендинг и «скачать» важнее.
const LegalPage = lazy(() => import('./pages/LegalPage'));

const MainMenuPage = lazy(() => import('./pages/MainMenuPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
// Рейтинг и турниры — своим куском: экран за входом, открывают реже стола.
const CompetePage = lazy(() => import('./pages/CompetePage'));
// Магазин — свой кусок: витрина с картинками комнат и аватарок.
const StorePage = lazy(() => import('./pages/StorePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PlayPage = lazy(() => import('./pages/PlayPage'));
const LobbyPage = lazy(() => import('./pages/LobbyPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const TableMockupPage = lazy(() => import('./pages/TableMockupPage'));
const AdminGate = lazy(() => import('./pages/Admin/AdminGate'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminComplaints = lazy(() => import('./pages/Admin/AdminComplaints'));
const AdminReports = lazy(() => import('./pages/Admin/AdminReports'));
const AdminAnalytics = lazy(() => import('./pages/Admin/AdminAnalytics'));
const AdminPlayers = lazy(() => import('./pages/Admin/AdminPlayers'));
const AdminHealth = lazy(() => import('./pages/Admin/AdminHealth'));
const AdminStub = lazy(() => import('./pages/Admin/AdminStub'));

// Пока кусок летит по сети — тёмный экран в тон приложения и одно слово. Белой вспышки
// быть не должно: на телефоне она читается как поломка.
const PageLoading = () => (
  <div style={{
    minHeight: '100vh', background: '#080C13', color: 'rgba(255,255,255,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Manrope, system-ui, sans-serif', fontSize: 15,
  }}>
    Загрузка…
  </div>
);

// Для /login и /register: если уже залогинен — на главную.
const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
    <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />

    <Route path="/" element={<LandingPage />} />

    {/* Страница установки — публичная, её кидают людям одной ссылкой */}
    <Route path="/get" element={<GetPage />} />
    <Route path="/app" element={<ProtectedRoute><MainMenuPage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
    <Route path="/compete" element={<ProtectedRoute><CompetePage /></ProtectedRoute>} />
    <Route path="/store" element={<ProtectedRoute><StorePage /></ProtectedRoute>} />
    <Route path="/lobby" element={<ProtectedRoute><LobbyPage /></ProtectedRoute>} />
    <Route path="/room/:id" element={<ProtectedRoute><RoomPage /></ProtectedRoute>} />
    {/* Приглашение открыто ДЛЯ ВСЕХ (08.09.2026): гостя, которого позвали в WhatsApp,
        нельзя встречать формой входа — он ещё никто. Страница сама решит: открыть
        приложение, предложить установку или (если человек свой) сесть за стол. */}
    <Route path="/join/:code" element={<JoinPage />} />
    <Route path="/play" element={<ProtectedRoute><PlayPage /></ProtectedRoute>} />

    {/* Командный центр /admin — за авторизацией + проверкой role=admin (гейт) */}
    <Route path="/admin" element={<ProtectedRoute><AdminGate><AdminDashboard /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/complaints" element={<ProtectedRoute><AdminGate><AdminComplaints /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/reports" element={<ProtectedRoute><AdminGate><AdminReports /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/analytics" element={<ProtectedRoute><AdminGate><AdminAnalytics /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/players" element={<ProtectedRoute><AdminGate><AdminPlayers /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/health" element={<ProtectedRoute><AdminGate><AdminHealth /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/finance" element={<ProtectedRoute><AdminGate><AdminStub title="Finance — финансы" icon="account_balance" note="Раздел появится с запуском биллинга/IAP. Пока финансовых данных нет." /></AdminGate></ProtectedRoute>} />
    <Route path="/admin/audit" element={<ProtectedRoute><AdminGate><AdminStub title="Audit — действия админов" icon="policy" note="Журнал админ-действий (кто смотрел PII, банил и т.д.). Наполнится когда появятся действия модерации." /></AdminGate></ProtectedRoute>} />

    {/* Мокап дизайна стола — без авторизации, для подбора вайба */}
    <Route path="/mockup" element={<TableMockupPage />} />

    {/* Юр. документы — публичный доступ, без авторизации */}
    {/* Удаление аккаунта — публичный адрес для карточки Google Play: путь удаления
        обязан быть виден человеку, который приложение ещё не поставил. */}
    <Route path="/delete-account" element={<DeleteAccountPage />} />

    <Route path="/privacy" element={<LegalPage slug="privacy" lang="ru" />} />
    <Route path="/terms" element={<LegalPage slug="terms" lang="ru" />} />
    <Route path="/eula" element={<LegalPage slug="eula" lang="ru" />} />
    <Route path="/rules" element={<LegalPage slug="rules" lang="ru" />} />
    <Route path="/community" element={<LegalPage slug="community" lang="ru" />} />
    <Route path="/en/privacy" element={<LegalPage slug="privacy" lang="en" />} />
    <Route path="/en/terms" element={<LegalPage slug="terms" lang="en" />} />
    <Route path="/en/eula" element={<LegalPage slug="eula" lang="en" />} />
    <Route path="/en/rules" element={<LegalPage slug="rules" lang="en" />} />
    <Route path="/en/community" element={<LegalPage slug="community" lang="en" />} />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      {/* Фотография-фон под всеми экранами — один слой на весь сайт, как в приложении.
          Стоит ДО страниц: они рисуются поверх и своей заливки не ставят. */}
      <AppBackdrop />
      {/* Suspense обязателен: без него ленивый кусок роняет дерево при первом переходе. */}
      <Suspense fallback={<PageLoading />}>
        <AppRoutes />
      </Suspense>
      <AgeGate />
      <InviteListener />
      <UpdatePrompt />
      <ConsentBanner />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
